import * as React from 'react'
import TopNavigation from '../components/Navigation/TopNavigation'
import { PlusIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { AnyAction, Dispatch } from 'redux'
import { connect } from 'react-redux'
import { AppState } from '../store'
import ScrollToTopOnMount from '../components/Effects/ScrollToTopOnMount'
import MemberItem from '../components/Team/MemberItem'
import AccountantConnectionItem from '../components/Team/AccountantConnectionItem'
import TeamController from '../controllers/TeamController'
import AccountantController from '../controllers/AccountantController'
import Notification from '../utilities/Notification'
import PageLoader from '../components/Page/PageLoader'
import { showAddAccountantModal, showConfirmModal, showUserWorkspaceSettingModal } from '../store/modals/actions'
import PageContent from '../components/Page/PageContent'
import { Helmet } from 'react-helmet'
import { withTranslation, WithTranslation } from 'react-i18next'
import UserWorkspaceSettingHelper from '../helpers/UserWorkspaceSettingHelper'
import { UserWorkspaceSettingsController } from '../controllers'
import { AccountantConnection, CurrentUser, UserWorkspaceSetting } from '../types'

interface IStateToProps {
  currentUser: CurrentUser
}

interface IDispatchToProps {
  showConfirmModal: typeof showConfirmModal
  showUserWorkspaceSettingModal: typeof showUserWorkspaceSettingModal
  showAddAccountantModal: typeof showAddAccountantModal
}

type IProps = IStateToProps & IDispatchToProps & WithTranslation

interface IState {
  didInitialLoad: boolean
  isFetching: boolean
  team: UserWorkspaceSetting[]
  accountants: AccountantConnection[]
}

class Team extends React.Component<IProps, IState> {
  constructor(props) {
    super(props);

    this.state = {
      didInitialLoad: false,
      isFetching: false,
      team: [],
      accountants: [],
    }

    this.onAddMemberClick = this.onAddMemberClick.bind(this)
    this.onAddAccountantClick = this.onAddAccountantClick.bind(this)
    this.onAcceptAccountantClick = this.onAcceptAccountantClick.bind(this)
    this.onDeleteAccountantClick = this.onDeleteAccountantClick.bind(this)
    this.onUserWorkspaceSettingModalSubmit = this.onUserWorkspaceSettingModalSubmit.bind(this)
  }

  onAddMemberClick() {
    this.props.showUserWorkspaceSettingModal({
      userWorkspaceSetting: {},
      onSubmit: this.onUserWorkspaceSettingModalSubmit
    })
  }

  onAddAccountantClick() {
    this.props.showAddAccountantModal({
      onSubmit: (connection: AccountantConnection) => {
        this.setState({ accountants: [...this.state.accountants, connection] })
      }
    })
  }

  onUserWorkspaceSettingModalSubmit(userWorkspaceSetting: UserWorkspaceSetting) {
    const { team } = this.state
    const teamMemberIndex = team.findIndex(member => member.id === userWorkspaceSetting.id)

    if (teamMemberIndex !== -1) {
      team[teamMemberIndex] = userWorkspaceSetting

      this.setState({ team: [...team] })
    } else {
      this.setState({ team: [...team, userWorkspaceSetting] })
    }
  }

  onEditTeamMemberClick(userWorkspaceSetting: UserWorkspaceSetting) {
    this.props.showUserWorkspaceSettingModal({
      userWorkspaceSetting: { id: userWorkspaceSetting.id },
      onSubmit: this.onUserWorkspaceSettingModalSubmit,
    })
  }

  onDeleteTeamMemberClick(member: UserWorkspaceSetting) {
    const { showConfirmModal, t } = this.props
    const { team } = this.state

    showConfirmModal({
      title: t('Team::Remove team member'),
      description: t('Team::You are about to remove team member <b>{{email}}</b>. Are you sure?', { email: member.email }),
      action: { label: t('Team::Confirm') },
      onConfirm: async () => {
        try {
          const response = await UserWorkspaceSettingsController.delete(member.id)

          const { success } = response

          if (success) {
            Notification.notifySuccess(t('Team::Team member succesfully removed'))

            const teamMemberIndex = team.findIndex(m => m.id === member.id)

            if (teamMemberIndex !== -1) {
              // Remove list from lists
              team.splice(teamMemberIndex, 1);

              this.setState({ team: [...team] })
            }
          } else {
            Notification.notifyError(t('Team::Oops something went wrong!'))
          }
        } catch (ex) {
          console.error(ex)
        }
      }
    })
  }

  async onAcceptAccountantClick(connection: AccountantConnection) {
    const { t } = this.props

    try {
      const response = await AccountantController.accept(connection.id)
      if (!response || response.errors || response.id !== connection.id) throw new Error('accept failed')

      this.setState({ accountants: this.state.accountants.map(c => c.id === connection.id ? response : c) })
      Notification.notifySuccess(t('Team::Accountant access granted'))
    } catch (ex) {
      console.error(ex)
      Notification.notifyError(t('Team::Oops something went wrong'))
    }
  }

  onDeleteAccountantClick(connection: AccountantConnection) {
    const { showConfirmModal, t } = this.props

    showConfirmModal({
      title: t('Team::Remove accountant'),
      description: t('Team::You are about to delete <b>{{email}}</b> from your team. Are you sure?', { email: connection.accountant?.full_name || connection.email }),
      action: { label: t('Team::Delete'), isDestructive: true },
      onConfirm: async () => {
        try {
          const response = await AccountantController.delete(connection.id)
          const { success } = response

          if (success) {
            this.setState({ accountants: this.state.accountants.filter(c => c.id !== connection.id) })

            Notification.notifySuccess(t('Team::Team member successfully removed'))
          } else {
            Notification.notifyError(t('Team::Oops something went wrong'))
          }
        } catch (ex) {
          console.error(ex)
          Notification.notifyError(t('Team::Oops something went wrong'))
        }
      }
    })
  }

  componentWillMount() {
    this.fetchTeam()
  }

  fetchTeam() {
    TeamController
      .getTeam()
      .then((response) => {
        const { team, accountants } = response

        this.setState({
          didInitialLoad: true,
          team: team,
          accountants: accountants
        })
      })
  }

  render() {
    const { t } = this.props
    const {
      didInitialLoad,
      team,
      accountants,
    } = this.state

    const { currentUser } = this.props
    const { workspace } = currentUser

    return (
      <>
        <Helmet>
          <title>{t('Team::{{__appName}} | Team')} </title>
        </Helmet>
        <ScrollToTopOnMount />

        <TopNavigation
          icon='team'
          title={t('Team::Team')}
        />

        <PageContent>
          {!didInitialLoad && <PageLoader />}
          {didInitialLoad && <>
            <h3 className='team-title'>{t('Team::Team')}</h3>

            <p className='team-description'>
              {(!workspace.business_name || (workspace.business_name && workspace.business_name.length === 0)) && t('Team::Manage who has access to this workspace or add new members to the team')}
              {workspace.business_name && workspace.business_name.length > 0 && t('Team::Manage who has access to {{name}} or add new members to the team', { name: workspace.business_name })}
            </p>

            <div className='mb-4'>
              <Button onClick={this.onAddMemberClick}>
                <PlusIcon data-icon='inline-start' />
                {t('Team::Add member')}
              </Button>
            </div>

            <div className='team-members'>
              {team.map(member => {
                const { role } = member
                return (
                  <MemberItem
                    key={member.id}
                    name={member?.user?.name ? member?.user?.name : member.email}
                    subheading={member.email}
                    status={member.status}
                    role={UserWorkspaceSettingHelper.getRoleTranslation(role)}
                    onEditClick={(member.user_id === currentUser.id) || !member.admin ? () => this.onEditTeamMemberClick(member) : null}
                    onDeleteClick={!member.admin ? () => this.onDeleteTeamMemberClick(member) : null}
                  />
                )
              })}
            </div>

            <h3 className='team-title'>{t('Team::Accountants')}</h3>

            <p className='team-description'>
              {t('Team::Invite your accountant or accept their access request. Accountants can view and export your invoices and expenses.')}
            </p>

            <div className='mb-4'>
              <Button onClick={this.onAddAccountantClick}>
                <PlusIcon data-icon='inline-start' />
                {t('Team::Invite accountant')}
              </Button>
            </div>

            <div className='team-members'>
              {accountants.map(connection => (
                <AccountantConnectionItem
                  key={connection.id}
                  connection={connection}
                  onAccept={this.onAcceptAccountantClick}
                  onRemove={this.onDeleteAccountantClick}
                />
              ))}
            </div>
          </>}
        </PageContent>
      </>
    )
  }
}

const mapStateToProps = (state: AppState): IStateToProps => {
  const {
    authentication: {
      currentUser,
    }
  } = state

  return {
    currentUser: currentUser,
  }
}

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>): IDispatchToProps => {
  return {
    showConfirmModal: (options) => dispatch(showConfirmModal(options)),
    showUserWorkspaceSettingModal: (options) => dispatch(showUserWorkspaceSettingModal(options)),
    showAddAccountantModal: (options) => dispatch(showAddAccountantModal(options))
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(Team))